import React, { Suspense, useContext, useEffect, useState, useRef } from "react";
import { lazy } from "react";
import {
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Prepare from "../screens/prepare";
import Leading from "../screens/leading";
import Login from "../screens/login";
import Signup from "../screens/signup";

import ForgotPassword from "../screens/forgotPassword";
import TestReview from "../screens/testReview";
import QuestionReview from "../screens/questionReview";
import Profile from "../screens/profile";
import Account from "../screens/account";
import TestSetting from "../screens/testSetting";
import OtherApps from "../screens/otherApps";
import HelpAndSupport from "../screens/helpAndSupport";
import MainLayout from "./layout/MainLayout";
import Glossary from "../screens/glossary";

import StudyQuestion from "../screens/studyQuestion";
import QuickSet from "../screens/quickSet";
import PracticeTest from "../screens/practiceTest";
import FlashCards from "../screens/flashCards";
import Bookmarks from "../screens/flashCards/BookMarks";
import QuickSets from "../screens/flashCards/QuickSets";
import CustomTestBuilder from "../screens/customTestBuilder";

import Dashboard from "../screens/dashboard";
import History from "../screens/history";
import Bookmark from "../screens/bookmark";
import Test from "../screens/test";
import MainBookmark from "../screens/MainBookmark";
import FlashCardExam from "../screens/flashCards/FlashCardExam";
import ChangeAppearance from "../screens/changeApperance";
import { auth } from "../api/config";
import PrepareBundle from "../screens/prepareBundle";
import { IS_BUNDLE_APP } from "../../app_constant";
import Loader from "./loader";
import Questions from "../screens/studyQuestion/questions";
import UpdateReadinessScore from "../screens/UpdateReadinessScore";
import UpdateAllQuestions from "../screens/UpdateAllQuestions";
import VolumePurchase from "../screens/choosePlans/VolumePurchase";
import ActivationSignup from "../screens/ActivationSignUp";
import ActivationVerification from "../screens/auth/ActivationVerification";
import { AuthContext } from "../context/AuthProvider";
import useFeatureList from "../hooks/useFeatureList";
import { createLogger } from "../utils/logger";

const Acronyms = lazy(() => import("../screens/acronyms"));
const Home = lazy(() => import("../screens/home"));
const ChoosePlan = lazy(() => import("../screens/choosePlans"));

const PageRouter = () => {
  const [user, setUser] = useState(null);
  const [isReloading, setIsReloading] = useState(false);
  const location = useLocation();
  const { isSubscribe, showReactivation, getSubscriptionDetails, loggingEnabled } = useContext(AuthContext);
  const lastCheckRef = useRef({});
  const featureList = useFeatureList();
  const logger = createLogger(loggingEnabled);
  
  logger.log('[Router] Render start - pathname:', location.pathname, 'user:', user, 'isReloading:', isReloading);

  const handleReload = () => {
    logger.log('[Router] handleReload triggered - setting isReloading to true');
    setIsReloading(true);
  };

  // Reset last check times when user changes
  useEffect(() => {
    lastCheckRef.current = {};
  }, [user]);

  useEffect(() => {
    logger.log('[Router] Component mounted - initial path:', location.pathname);
    logger.log('[Router] Setting up auth listener');
    const unsubscribeAuth = auth().onAuthStateChanged((authUser) => {
      logger.log('[Router] onAuthStateChanged - user is', authUser ? 'logged in' : 'logged out', authUser);
      setUser(authUser || false);
    });
    window.addEventListener("beforeunload", handleReload);
    return () => {
      logger.log('[Router] Cleanup - removing auth listener and beforeunload listener');
      unsubscribeAuth();
      window.removeEventListener("beforeunload", handleReload);
    };
  }, []);

  const shouldCheckSubscription = (pathname, userId) => {
    const pathKey = `${pathname}-${userId}`;
    const lastCheck = lastCheckRef.current[pathKey];
    const now = Date.now();
    
    // Check if this is the first check or if enough time has passed since last check
    // Recheck every hour
    const shouldCheck = !lastCheck || (now - lastCheck) > 60 * 60 * 1000;
    
    if (shouldCheck) {
      lastCheckRef.current[pathKey] = now;
    }
    
    return shouldCheck;
  };

  useEffect(() => {
    if (!user?.uid) return;

    logger.log('[Router] Pathname changed - checking if subscription check needed for', location.pathname);
    
    // Paths that need subscription verification
    const pathsThatNeedSubscription = ['/home', '/prepare', '/chooseplan', '/studyQuestion'];
    
    if (pathsThatNeedSubscription.includes(location.pathname) && 
        shouldCheckSubscription(location.pathname, user.uid)) {
      logger.log('[Router] Checking subscription status');
      
      getSubscriptionDetails()
        .then(() => logger.log('[Router] Subscription details fetched successfully'))
        .catch(err => logger.error('[Router] Error fetching subscription details:', err));
    }
  }, [location.pathname, user, getSubscriptionDetails]);

  // Set up periodic subscription check
  useEffect(() => {
    if (!user?.uid) return;

    const checkInterval = setInterval(() => {
      logger.log('[Router] Running periodic subscription check');
      getSubscriptionDetails()
        .then(() => logger.log('[Router] Periodic subscription check completed'))
        .catch(err => logger.error('[Router] Error in periodic subscription check:', err));
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(checkInterval);
  }, [user, getSubscriptionDetails]);

  logger.log('[Router] Rendering - isReloading:', isReloading, 'user:', !!user);
  return (
    <>
      {isReloading ? (
        (() => { logger.log('[Router] Showing Loader due to reload'); return <Loader />; })()
      ) : (
        <>
        {user == null ? <Loader/> : <>
        {!user ? (
            (() => { logger.log('[Router] Rendering unauthenticated routes');
              return (
                <Suspense fallback={<Loader />}>
                  <Routes>
                    {/* Unauth routes */}
                    <Route path="*" element={<ActivationSignup />} />
                    <Route path="/leading" element={<Leading />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup/activation" element={<ActivationSignup />} />
                    <Route path="/signup/verification" element={<ActivationVerification />} />
                    {featureList?.signup.email && <Route path="/signup" element={<Signup />} />}
                    <Route path="/forgot_password" element={<ForgotPassword />} />
                    <Route
                      path="/prepare"
                      element={
                        IS_BUNDLE_APP
                          ? <PrepareBundle handleReload={handleReload} />
                          : <Prepare />
                      }
                    />
                  </Routes>
                </Suspense>
              );
            })()
          ) : (
            (() => { logger.log('[Router] Rendering authenticated routes');
              return (
                <>
                  {isReloading ? (
                    (() => { logger.log('[Router] Authenticated reload - showing Loader'); return <Loader />; })()
                  ) : (
                    <Suspense fallback={<Loader />}>
                      <Routes>
                        {/* Shared user routes */}
                        <Route path="/prepare" element={IS_BUNDLE_APP ? <PrepareBundle handleReload={handleReload} /> : <Prepare />} />
                        <Route path="/updaterediness" element={<UpdateReadinessScore />} />
                        <Route path="/updateallquestions" element={<UpdateAllQuestions />} />
                        <Route element={<MainLayout />}>
                          {/* MainLayout nested routes */}
                          <Route path="/choosePlan" element={isReloading ? <Loader /> : <ChoosePlan />} />
                          <Route path="/volumePurchase" element={<VolumePurchase />} />
                          <Route path="/testReview" element={<TestReview />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/account" element={<Account />} />
                          <Route path="/appearance" element={<ChangeAppearance />} />
                          <Route path="/testSetting" element={<TestSetting />} />
                          <Route path="/otherApps" element={<OtherApps />} />
                          <Route path="/helpAndSupport" element={<HelpAndSupport />} />
                          <Route path="/glossary" element={<Glossary />} />
                          <Route path="/acronymns" element={<Acronyms />} />
                          <Route path="/studyQuestion" element={<StudyQuestion />} />
                          <Route path="/quick" element={<QuickSet />} />
                          <Route path="/practicetest" element={<PracticeTest />} />
                          <Route path="/flashcard" element={<FlashCards />} />
                          <Route path="/flashcard/questions" element={<QuickSets />}/>
                          <Route path="/flashcard/bookmark" element={<Bookmarks />} />
                          <Route path="/flashcard/quick" element={<QuickSets />} />
                          <Route path="/flashcard/exam" element={<FlashCardExam />} />
                          <Route path="/customtest" element={<CustomTestBuilder />} />
                          <Route path="/home" element={<Home />} />
                          <Route path="/studyquestions/:id" element={<Questions />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/history" element={<History />} />
                          <Route path="/Bookmark" element={<MainBookmark />} />
                          <Route path="/Bookmark/questions" element={<Bookmark />} />
                          <Route path="/test" element={<Test />} />
                          <Route path="/questionReview" element={<QuestionReview />} />
                          <Route path="/signup/activation" element={<ActivationSignup />} />
                          <Route path="/" element={<Home />} />
                          <Route path="*" element={<Home />} />
                        </Route>
                      </Routes>
                    </Suspense>
                  )}
                </>
              );
            })()
          )}
        </>}
          
        </>
      )}
    </>
  );
};

export default PageRouter;
